import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Download, FileDown, Loader2, ShieldAlert, Table2 } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import { INCIDENT_CATEGORY_LABELS } from "@/config/incidentCatalog";

const PERIODS = [
  { value: "today", label: "Today" }, { value: "week", label: "This week" },
  { value: "month", label: "This month" }, { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" }, { value: "custom", label: "Custom range" }, { value: "", label: "All time" },
];

const KPI_LABELS = {
  total_referrals: "Total Referrals", new_referrals: "New Referrals", referrals_awaiting_review: "Referrals Awaiting Review",
  initial_assessments_due: "Initial Assessments Due", active_programmes: "Active Programmes",
  paused_programmes: "Paused Programmes", extended_programmes: "Extended Programmes",
  final_assessments_due: "Final Assessments Due", programmes_completed: "Programmes Completed",
  certificates_issued: "Certificates Issued", sessions_delivered: "Sessions Delivered",
  sessions_cancelled: "Cancelled Sessions", attendance_rate: "Attendance Rate %",
  avg_programme_weeks: "Avg Programme Duration (weeks)", risk_reviews_outstanding: "Risk Reviews Outstanding",
  open_incidents: "Open Incidents", near_misses: "Near Misses", outstanding_actions: "Outstanding Management Actions",
};

function useReport(path, params, dep) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(path, { params: Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v)) })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
  return data;
}

function PeriodFilter({ value, onChange, from, to, onFrom, onTo, councilId, onCouncil, councils }) {
  return (
    <Card><CardContent className="p-4 flex flex-wrap gap-3 items-end">
      <div className="space-y-1">
        <Label className="text-xs">Period</Label>
        <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="report-period" className="h-11 w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{PERIODS.map((p) => <SelectItem key={p.value || "all"} value={p.value || "all"}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {value === "custom" && (
        <>
          <div className="space-y-1"><Label className="text-xs">From</Label><Input data-testid="report-from" type="date" className="h-11" value={from} onChange={(e) => onFrom(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input data-testid="report-to" type="date" className="h-11" value={to} onChange={(e) => onTo(e.target.value)} /></div>
        </>
      )}
      {onCouncil && (
        <div className="space-y-1">
          <Label className="text-xs">Local Authority</Label>
          <Select value={councilId || "all"} onValueChange={(v) => onCouncil(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="report-council" className="h-11 w-56"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All authorised Local Authorities</SelectItem>
              {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </CardContent></Card>
  );
}

function DataTable({ columns, rows, testid, emptyKey }) {
  if (!rows?.length) return <p className="text-sm text-slate-600" data-testid={`${testid}-empty`}>No data available for the selected period.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-xs text-slate-500 border-b border-border">
          {columns.map((c) => <th key={c.key || c} className="py-2 pr-3">{(c.label || c.key || c).replace(/_/g, " ")}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/50" data-testid={`${testid}-row-${r[emptyKey] || i}`}>
              {columns.map((c) => <td key={c.key || c} className="py-2 pr-3">{c.render ? c.render(r) : (r[c.key ?? c] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportButton({ reportType, filters, testid }) {
  const { hasPermission } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!hasPermission("reports:export")) return null;
  const run = async (fileType) => {
    setBusy(true);
    try {
      const { data } = await api.post("/exports", { report_type: reportType, filters: filters || {}, file_type: fileType });
      toast.success(`Export ${data.export_number} created — opening`);
      window.open(`${API_URL}/exports/${data.id}/download`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex gap-1">
      {["pdf", "csv", "xlsx"].map((f) => (
        <Button key={f} size="sm" variant="outline" data-testid={`${testid}-${f}`} disabled={busy} onClick={() => run(f)} className="h-9">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}{f.toUpperCase()}
        </Button>
      ))}
    </span>
  );
}

const TABS = [
  ["executive", "Executive Dashboard"], ["councils", "Local Authority Reports"], ["referrals", "Referral Analytics"],
  ["delivery", "Training Delivery"], ["attendance", "Attendance"], ["duration", "Programme Duration"],
  ["outcomes", "Outcomes"], ["progress", "Progress"], ["incidents", "Incident Analytics"],
  ["workload", "Staff Workload"], ["compliance", "Staff Compliance"], ["certificates", "Certificates"],
  ["quality", "Data Quality"], ["feedback", "Feedback & QA"], ["actions", "Action Centre"], ["custom", "Custom Reports"], ["exports", "Export Centre"],
];

const PARENT_RATING_LABELS = {
  professionalism: "Trainer professionalism & training", dignity_respect: "Dignity & respect for the child",
  timekeeping: "Trainer timekeeping", office_communication: "Office communication",
  consistency: "Consistency of training", sensitivity_needs: "Sensitivity to personal/cultural needs",
  overall_impression: "Overall impression", involvement_in_plan: "Involvement in training plan",
  complaints_response: "Response to complaints",
};

export default function ReportsPage() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState("executive");
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [councilId, setCouncilId] = useState("");
  const [councils, setCouncils] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const params = useMemo(() => ({
    period: period || undefined,
    date_from: period === "custom" ? from : undefined,
    date_to: period === "custom" ? to : undefined,
    council_id: councilId || undefined,
  }), [period, from, to, councilId]);
  const dep = JSON.stringify(params) + tab;

  useEffect(() => {
    api.get("/councils").then((r) => setCouncils(r.data.items || [])).catch(() => {});
  }, []);

  const exec = useReport("/reports/executive", params, tab === "executive" ? dep : "skip");
  const councilData = useReport("/reports/councils", params, tab === "councils" ? dep : "skip");
  const referrals = useReport("/reports/referrals", params, tab === "referrals" ? dep : "skip");
  const pipeline = useReport("/reports/pipeline", params, tab === "referrals" ? dep : "skip");
  const delivery = useReport("/reports/delivery", params, tab === "delivery" ? dep : "skip");
  const attendance = useReport("/reports/attendance", params, tab === "attendance" ? dep : "skip");
  const duration = useReport("/reports/duration", params, tab === "duration" ? dep : "skip");
  const outcomes = useReport("/reports/outcomes", params, tab === "outcomes" ? dep : "skip");
  const progress = useReport("/reports/progress", params, tab === "progress" ? dep : "skip");
  const incidentData = useReport("/reports/incidents", params, tab === "incidents" ? dep : "skip");
  const workload = useReport("/reports/staff-workload", params, tab === "workload" ? dep : "skip");
  const compliance = useReport("/reports/staff-compliance", params, tab === "compliance" ? dep : "skip");
  const certificates = useReport("/reports/certificates", params, tab === "certificates" ? dep : "skip");
  const quality = useReport("/reports/data-quality", params, tab === "quality" ? dep : "skip");
  const actionCentre = useReport("/reports/action-centre", params, tab === "actions" ? dep : "skip");
  const feedback = useReport("/reports/feedback", params, tab === "feedback" ? dep : "skip");
  const exportList = useReport("/exports", params, tab === "exports" ? dep : "skip");
  const customAreas = useReport("/reports/custom/areas", {}, tab === "custom" ? "load" : "skip");

  const [customArea, setCustomArea] = useState("");
  const [customFields, setCustomFields] = useState([]);
  const [customResult, setCustomResult] = useState(null);
  const [customBusy, setCustomBusy] = useState(false);

  if (!hasPermission("reports:executive")) {
    return (
      <div data-testid="reports-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600 max-w-md">Reports &amp; Analytics is restricted to authorised management roles.</p>
      </div>
    );
  }

  const runCustom = async () => {
    setCustomBusy(true);
    try {
      const { data } = await api.post("/reports/custom", {
        area: customArea, fields: customFields,
        date_from: period === "custom" ? from : undefined, date_to: period === "custom" ? to : undefined,
        council_id: councilId || undefined,
      });
      setCustomResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCustomBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-primary" /> Reports &amp; Analytics
        </h1>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Month for monthly reports</Label>
            <Input data-testid="report-month" type="month" className="h-11" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <a href={`${API_URL}/reports/monthly-management.pdf?month=${month}`} target="_blank" rel="noreferrer" data-testid="monthly-management-pdf"
            className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
            <FileDown className="h-4 w-4" /> Monthly management report
          </a>
        </div>
      </div>

      <PeriodFilter value={period} onChange={setPeriod} from={from} to={to} onFrom={setFrom} onTo={setTo}
        councilId={councilId} onCouncil={setCouncilId} councils={councils} />

      <div className="flex flex-wrap gap-1.5" data-testid="reports-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`reports-tab-${key}`} onClick={() => setTab(key)}
            className={`h-10 px-3 rounded-md text-xs font-semibold transition-colors duration-150 ${tab === key ? "bg-primary text-white" : "bg-white text-slate-600 border border-border hover:bg-muted"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "executive" && (
        <div data-testid="report-executive">
          {!exec ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
            <>
              <p className="text-xs text-slate-500" data-testid="exec-scope">
                {exec.council_filter} · period {exec.period.start || "all time"}{exec.period.end ? ` → ${exec.period.end}` : ""} · date definitions: {Object.entries(exec.period.definitions).map(([k, v]) => `${k} = ${v}`).join(", ")}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
                {Object.entries(KPI_LABELS).map(([k, label]) => (
                  <Card key={k} data-testid={`kpi-${k}`}>
                    <CardContent className="p-3">
                      <p className="text-xl font-bold text-slate-900">{exec.kpis[k] ?? "—"}</p>
                      <p className="text-[11px] text-slate-500">{label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {exec.safeguarding && (
                <Card className="mt-3 border-red-200" data-testid="exec-safeguarding">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold text-red-700 uppercase mb-1">Safeguarding (restricted — counts only)</p>
                    <p className="text-sm text-slate-700">Open concerns: {exec.safeguarding.open_concerns} · Urgent: {exec.safeguarding.urgent}</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {tab === "councils" && (
        <Card data-testid="report-councils">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Local Authority performance</CardTitle>
            <span className="flex items-center gap-2">
              <ExportButton reportType="council_outcome" filters={params} testid="export-council" />
              {councilId && (
                <a href={`${API_URL}/reports/la-monthly.pdf?month=${month}&council_id=${councilId}`} target="_blank" rel="noreferrer" data-testid="la-monthly-pdf"
                  className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold hover:bg-muted">
                  <FileDown className="h-3.5 w-3.5" /> LA monthly PDF
                </a>
              )}
            </span>
          </CardHeader>
          <CardContent>
            {!councilData ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <DataTable testid="council-table" emptyKey="council"
                columns={["council", "total_referrals", "referrals_in_period", "assessments_completed", "active_programmes", "paused_programmes", "completed_programmes", "sessions_delivered", "attendance_rate", "avg_programme_weeks", "final_assessments", "successful_completions", "further_training_required", "certificates_issued", "open_actions"]}
                rows={councilData.items} />
            )}
          </CardContent>
        </Card>
      )}

      {tab === "referrals" && (
        <div className="space-y-4" data-testid="report-referrals">
          <Card>
            <CardHeader><CardTitle className="text-lg">Referral pipeline</CardTitle></CardHeader>
            <CardContent>
              {!pipeline ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
                <>
                  <div className="flex flex-wrap gap-2" data-testid="pipeline-stages">
                    {pipeline.stages.map((s) => (
                      <div key={s.stage} className="rounded-md border border-border px-3 py-2 text-center" data-testid={`pipeline-${s.stage}`}>
                        <p className="text-lg font-bold text-primary">{s.count}</p>
                        <p className="text-[11px] text-slate-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {pipeline.aging?.length > 0 && (
                    <div className="mt-3" data-testid="pipeline-aging">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Cases at the same stage for 30+ days (factual — for management review, not blame)</p>
                      {pipeline.aging.map((a) => (
                        <p key={a.case_id} className="text-xs text-slate-600">{a.case_id} · {a.stage.replace(/_/g, " ")} · {a.days} days</p>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-slate-500">{pipeline.note}</p>
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg">Referral report</CardTitle>
              <ExportButton reportType="referral_report" filters={params} testid="export-referrals" />
            </CardHeader>
            <CardContent>
              {!referrals ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
                <DataTable testid="referral-table" emptyKey="case_id"
                  columns={["case_id", "referral_id", "child", "council", "school", "referral_date", "referral_status", "case_status", "assigned_trainer", "programme_status", "outcome"]}
                  rows={referrals.items} />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "delivery" && (
        <Card data-testid="report-delivery">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Training delivery</CardTitle>
            <ExportButton reportType="delivery" filters={params} testid="export-delivery" />
          </CardHeader>
          <CardContent>
            {!delivery ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <DataTable testid="delivery-table" emptyKey="programme_number"
                columns={["programme_number", "council", "child", "case_id", "trainer", "start_date", "target_completion_date", "sessions_planned", "sessions_completed", "sessions_cancelled", "current_week", "overall_level_outbound", "overall_level_return", "status"]}
                rows={delivery.items} />
            )}
          </CardContent>
        </Card>
      )}

      {tab === "attendance" && (
        <Card data-testid="report-attendance">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Attendance &amp; cancellations</CardTitle>
            <ExportButton reportType="attendance" filters={params} testid="export-attendance" />
          </CardHeader>
          <CardContent>
            {!attendance ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <div className="h-64" data-testid="attendance-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendance.breakdown}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="attendance" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} /><Tooltip />
                      <Bar dataKey="count" fill="#005EB8" /></BarChart>
                  </ResponsiveContainer>
                </div>
                <DataTable testid="attendance-table" emptyKey="attendance" columns={["attendance", "count", "percentage"]} rows={attendance.breakdown} />
                <p className="mt-2 text-xs text-slate-500">{attendance.note}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "feedback" && (
        <div className="space-y-4" data-testid="report-feedback">
          {!feedback ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="feedback-counts">
                {[["trainee", "Trainee surveys"], ["parent_8weekly", "Parent 8-weekly"], ["parent_final", "Parent final"], ["parent_signoff", "Sign-offs"], ["total", "Total forms"]].map(([k, l]) => (
                  <Card key={k} data-testid={`feedback-count-${k}`}>
                    <CardContent className="p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">{feedback.counts?.[k] ?? 0}</p>
                      <p className="text-xs text-slate-500">{l}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card data-testid="feedback-confidence">
                <CardHeader><CardTitle className="text-lg">Trainee confidence (self-reported, 1–10)</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="feedback-confidence-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { name: "Before training", value: feedback.trainee?.avg_confidence_before ?? 0 },
                        { name: "After training", value: feedback.trainee?.avg_confidence_after ?? 0 },
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis domain={[0, 10]} /><Tooltip />
                        <Bar dataKey="value" fill="#0E7490" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                    <p data-testid="feedback-uplift">Average uplift: <b>{feedback.trainee?.avg_uplift ?? "—"}</b></p>
                    <p data-testid="feedback-recommend">Would recommend: <b>{feedback.trainee?.would_recommend_pct != null ? `${feedback.trainee.would_recommend_pct}%` : "—"}</b></p>
                    <p data-testid="feedback-made-trip">Made the trained trip: <b>{feedback.trainee?.made_trip_pct != null ? `${feedback.trainee.made_trip_pct}%` : "—"}</b></p>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{feedback.trainee?.denominator_note}</p>
                </CardContent>
              </Card>

              <Card data-testid="feedback-parent-ratings">
                <CardHeader><CardTitle className="text-lg">Parent ratings (8-weekly + end-of-programme)</CardTitle></CardHeader>
                <CardContent>
                  <DataTable testid="feedback-ratings-table" emptyKey="item" columns={["item", "average", "responses"]}
                    rows={(feedback.parent_ratings || []).map((r) => ({ ...r, item: PARENT_RATING_LABELS[r.item] || r.item }))} />
                  <p className="mt-2 text-xs text-slate-500">{feedback.rating_scale}. Averages use only submitted ratings.</p>
                </CardContent>
              </Card>

              <Card data-testid="feedback-per-trainer">
                <CardHeader><CardTitle className="text-lg">Per staff member</CardTitle></CardHeader>
                <CardContent>
                  <DataTable testid="feedback-trainer-table" emptyKey="staff" columns={["staff", "forms_recorded", "trainee_forms", "avg_confidence_uplift"]} rows={feedback.per_trainer || []} />
                </CardContent>
              </Card>

              <Card data-testid="feedback-per-council">
                <CardHeader><CardTitle className="text-lg">Per Local Authority</CardTitle></CardHeader>
                <CardContent>
                  <DataTable testid="feedback-council-table" emptyKey="council_name" columns={["council_name", "forms", "avg_confidence_uplift"]} rows={feedback.per_council || []} />
                </CardContent>
              </Card>
              <p className="text-xs text-slate-500">{feedback.note}</p>
            </>
          )}
        </div>
      )}

      {tab === "duration" && (
        <Card data-testid="report-duration">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Programme duration</CardTitle>
            <ExportButton reportType="duration" filters={params} testid="export-duration" />
          </CardHeader>
          <CardContent>
            {!duration ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <p className="text-sm text-slate-700" data-testid="duration-stats">
                  Average: <b>{duration.average_weeks ?? "n/a"} weeks</b> · Median: <b>{duration.median_weeks ?? "n/a"} weeks</b>
                  {" "}· Distribution: {Object.entries(duration.distribution || {}).map(([k, v]) => `${k}w: ${v}`).join(" · ")}
                </p>
                <DataTable testid="duration-table" emptyKey="programme_number"
                  columns={["programme_number", "council", "start_date", "original_target", "revised_target", "actual_completion", "weeks", { key: "extended", render: (r) => (r.extended ? "Yes" : "No") }, { key: "extension_reasons", render: (r) => (r.extension_reasons || []).join(", ") || "—" }, "pause_count"]}
                  rows={duration.items} />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "outcomes" && (
        <Card data-testid="report-outcomes">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Outcomes</CardTitle>
            <ExportButton reportType="outcomes" filters={params} testid="export-outcomes" />
          </CardHeader>
          <CardContent>
            {!outcomes ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="outcomes-counts">
                  {Object.entries(outcomes.counts).map(([k, v]) => (
                    <div key={k} className="rounded-md border border-border p-3" data-testid={`outcome-${k}`}>
                      <p className="text-xl font-bold text-slate-900">{v}</p>
                      <p className="text-[11px] text-slate-500">{k.replace(/_/g, " ")}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-sm text-slate-700" data-testid="outcomes-denominator">
                  {outcomes.denominator.label}: <b>{outcomes.denominator.completion_rate ?? "n/a"}%</b> ({outcomes.denominator.completed}/{outcomes.denominator.closed})
                </p>
                <p className="text-xs text-slate-500">{outcomes.note}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "progress" && (
        <Card data-testid="report-progress">
          <CardHeader><CardTitle className="text-lg">Progress analytics (initial → final independence levels)</CardTitle></CardHeader>
          <CardContent>
            {!progress ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <DataTable testid="progress-table" emptyKey="area"
                  columns={[{ key: "area", render: (r) => r.area.replace(/_/g, " ") }, "n", "avg_initial", "avg_final", "moved_up", "no_change", "moved_down"]}
                  rows={progress.areas} />
                <p className="mt-2 text-xs text-slate-500">{progress.scale}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "incidents" && (
        <Card data-testid="report-incidents">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Incident analytics</CardTitle>
            <ExportButton reportType="incident_analytics" filters={params} testid="export-incidents" />
          </CardHeader>
          <CardContent>
            {!incidentData ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  {[["total", "Incidents"], ["near_misses", "Near misses"], ["closed", "Closed"], ["open_actions", "Open actions"], ["risk_reviews_triggered", "Risk reviews triggered"], ["journey_reviews_triggered", "Journey reviews triggered"]].map(([k, l]) => (
                    <div key={k} className="rounded-md border border-border p-3" data-testid={`incident-stat-${k}`}>
                      <p className="text-xl font-bold text-slate-900">{incidentData[k]}</p>
                      <p className="text-[11px] text-slate-500">{l}</p>
                    </div>
                  ))}
                </div>
                {incidentData.by_month?.length > 0 && (
                  <div className="h-56 mb-3" data-testid="incident-trend">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={incidentData.by_month}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} /><Tooltip /><Legend />
                        <Line type="monotone" dataKey="count" name="Incidents" stroke="#DA291C" strokeWidth={2} /></LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <DataTable testid="incident-category-table" emptyKey="category"
                  columns={[{ key: "category", render: (r) => INCIDENT_CATEGORY_LABELS[r.category] || r.category }, "count"]}
                  rows={incidentData.by_category} />
                <p className="mt-2 text-xs text-slate-500">{incidentData.note}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "workload" && (
        <Card data-testid="report-workload">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Staff workload</CardTitle>
            <ExportButton reportType="staff_workload" filters={{}} testid="export-workload" />
          </CardHeader>
          <CardContent>
            {!workload ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <DataTable testid="workload-table" emptyKey="trainer"
                  columns={["trainer", "active_children", "active_programmes", "sessions_scheduled", "sessions_completed", "outstanding_notes", "assessments_in_progress", "final_assessments_assigned", "cover_assignments"]}
                  rows={workload.items} />
                <p className="mt-2 text-xs text-slate-500" data-testid="workload-note">{workload.note}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "compliance" && (
        <Card data-testid="report-compliance">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Staff compliance</CardTitle>
            <ExportButton reportType="staff_compliance" filters={{}} testid="export-compliance" />
          </CardHeader>
          <CardContent>
            {!compliance ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <DataTable testid="compliance-table" emptyKey="staff"
                columns={["staff", "role", "employment_status",
                  ...(compliance.required_types || []).map((t) => ({
                    key: t, label: t.replace(/_/g, " "),
                    render: (r) => {
                      const v = r.compliance?.[t] || "missing";
                      return <Badge className={v === "valid" ? "bg-ukgreen-light text-ukgreen border-transparent" : v === "expiring_soon" ? "bg-amber-100 text-amber-800 border-transparent" : "bg-red-50 text-red-700 border-transparent"}>{v.replace(/_/g, " ")}</Badge>;
                    },
                  })),
                  "alerts"]}
                rows={compliance.items} />
            )}
          </CardContent>
        </Card>
      )}

      {tab === "certificates" && (
        <Card data-testid="report-certificates">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg">Certificates</CardTitle>
            <ExportButton reportType="certificates" filters={params} testid="export-certificates" />
          </CardHeader>
          <CardContent>
            {!certificates ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <DataTable testid="certificates-table" emptyKey="certificate_number"
                columns={["certificate_number", "case_id", "child", "council", "programme", "issue_date", "version", "status", "issued_by"]}
                rows={certificates.items} />
            )}
          </CardContent>
        </Card>
      )}

      {tab === "quality" && (
        <Card data-testid="report-quality">
          <CardHeader><CardTitle className="text-lg">Data quality</CardTitle></CardHeader>
          <CardContent>
            {!quality ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <div className="space-y-1.5">
                  {(quality.items || []).map((q, i) => (
                    <Link key={i} to={q.link} data-testid={`quality-${i}`}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors duration-150 ${q.severity === "high" ? "border-red-200 bg-red-50 text-red-900" : "border-border hover:bg-muted"}`}>
                      <Badge className={q.severity === "high" ? "bg-red-600 text-white border-transparent" : "bg-slate-100 text-slate-600 border-transparent"}>{q.severity}</Badge>
                      <span className="flex-1">{q.title}</span>
                    </Link>
                  ))}
                </div>
                {!quality.items?.length && <p className="text-sm text-slate-600" data-testid="quality-empty">No data-quality issues found.</p>}
                <p className="mt-2 text-xs text-slate-500">{quality.note}</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "actions" && (
        <Card data-testid="report-actions">
          <CardHeader><CardTitle className="text-lg">Management action centre {actionCentre ? `(${actionCentre.counts?.total ?? 0}, ${actionCentre.counts?.high ?? 0} high priority)` : ""}</CardTitle></CardHeader>
          <CardContent>
            {!actionCentre ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <div className="space-y-1.5">
                {(actionCentre.items || []).map((t, i) => (
                  <Link key={i} to={t.link} data-testid={`action-${i}`}
                    className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors duration-150 ${t.priority === "high" ? "border-red-200 bg-red-50" : "border-border hover:bg-muted"}`}>
                    <Badge className={t.priority === "high" ? "bg-red-600 text-white border-transparent" : "bg-slate-100 text-slate-600 border-transparent"}>{t.priority}</Badge>
                    <span className="flex-1">{t.title}</span>
                    {t.owner && <span className="text-xs text-slate-500">{t.owner}</span>}
                    {t.due_date && <span className="text-xs text-slate-500">due {t.due_date}</span>}
                  </Link>
                ))}
                {!actionCentre.items?.length && <p className="text-sm text-slate-600" data-testid="actions-empty">No outstanding management actions.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "custom" && (
        <Card data-testid="report-custom">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Table2 className="h-5 w-5 text-primary" /> Custom report builder</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Approved reportable fields only. Safeguarding data is never available here.</p>
            {!customAreas ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <>
                <Select value={customArea} onValueChange={(v) => { setCustomArea(v); setCustomFields([]); setCustomResult(null); }}>
                  <SelectTrigger data-testid="custom-area" className="h-11 w-64"><SelectValue placeholder="Select data area" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {customAreas.areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {customArea && (
                  <div className="flex flex-wrap gap-2" data-testid="custom-fields">
                    {customAreas.areas.find((a) => a.key === customArea)?.fields.map((f) => (
                      <button key={f} type="button" data-testid={`custom-field-${f}`}
                        onClick={() => setCustomFields((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]))}
                        className={`h-10 px-3 rounded-md border text-xs font-medium ${customFields.includes(f) ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-border hover:bg-muted"}`}>
                        {f.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button data-testid="custom-run" className="h-11 bg-primary text-white font-semibold" disabled={customBusy || !customArea || !customFields.length} onClick={runCustom}>
                    {customBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}
                  </Button>
                  {customResult && (
                    <ExportButton reportType="custom" filters={{ area: customArea, fields: customFields, council_id: councilId || undefined }} testid="export-custom" />
                  )}
                </div>
                {customResult && (
                  <DataTable testid="custom-table" emptyKey={customFields[0]}
                    columns={customFields} rows={customResult.rows} />
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "exports" && (
        <Card data-testid="report-exports">
          <CardHeader><CardTitle className="text-lg">Export centre</CardTitle></CardHeader>
          <CardContent>
            {!exportList ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
              <DataTable testid="exports-table" emptyKey="export_number"
                columns={[
                  "export_number", "report_type", "file_type", "created_by_name",
                  { key: "created_at", render: (r) => (r.created_at || "").slice(0, 16).replace("T", " ") },
                  "status", "download_count",
                  { key: "actions", label: "Download", render: (r) => r.status !== "expired" ? (
                    <a href={`${API_URL}/exports/${r.id}/download`} target="_blank" rel="noreferrer" data-testid={`export-download-${r.export_number}`}
                      className="text-primary text-xs font-semibold hover:underline">Download</a>
                  ) : <span className="text-xs text-slate-400">Expired</span> },
                ]}
                rows={exportList.items} />
            )}
            <p className="mt-2 text-xs text-slate-500">Exports expire after 7 days. Every creation and download is audit-logged.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
